// src/pages/AdminComentarios.js
import React, { useEffect, useState, useRef } from "react";
import {
  adminFetchPosts,
  adminFetchReplies,
  adminApprove,
  adminReject,
  adminSoftDelete,
  adminHardDelete,
  adminBlockUser,
  adminUnblockUser,
  adminApproveReply,
  adminRejectReply,
  adminSoftDeleteReply,
} from "../services/timelineAdmin";
import { normalizeDate } from "../services/timelineService";
import nomesBonitosCategoria from "../utils/nomesBonitosCategoria";
import "./AdminComentarios.css";

const ICONS = { timeline: "https://i.imgur.com/zhvk1oW.png" };

function formatPtDate(dt) {
  if (!dt) return "";
  try {
    const d = normalizeDate(dt);
    const dia = d.toLocaleDateString("pt-PT", { day: "2-digit" });
    const mes = d.toLocaleDateString("pt-PT", { month: "long" });
    const hora = d.toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" });
    return `${dia} de ${mes}, às ${hora}`;
  } catch { return ""; }
}

function prettyModo(m) {
  if (m === "oscar-indicados") return "Indicados ao Oscar";
  if (m === "oscar-vencedores") return "Vencedores do Oscar";
  if (m === "globodeouro-indicados") return "Indicados ao Globo de Ouro";
  if (m === "globodeouro-vencedores") return "Vencedores do Globo de Ouro";
  if (m === "sagawards-indicados") return "Indicados ao SAG Awards";
  if (m === "sagawards-vencedores") return "Vencedores ao SAG Awards";
  if (m === "criticschoice-indicados") return "Indicados ao Critics Choice";
  if (m === "criticschoice-vencedores") return "Vencedores ao Critics Choice";
  if (m === "dga-indicados") return "Indicados ao DGA Awards";
  if (m === "dga-vencedoress") return "Vencedores ao DGA Awards";
  if (m === "pga-indicados") return "Indicados ao PGA Awards";
  if (m === "pga-vencedores") return "Vencedores ao PGA Awards";
  if (m === "bafta-indicados") return "Indicados ao BAFTA";
  if (m === "bafta-vencedores") return "Vencedores ao BAFTA";
  return m || "";
}
function prettyCategoria(c) {
  if (!c) return "";
  const key = String(c).toLowerCase().replace(/\s+/g, "");
  return nomesBonitosCategoria[key] || c;
}
function StatusPill({ status }) {
  const map = {
    published: { label: "Publicado", cls: "ok" },
    pending:   { label: "Pendente",  cls: "pen" },
    rejected:  { label: "Rejeitado", cls: "rej" },
    deleted:   { label: "Apagado",   cls: "del" },
  };
  const v = map[status] || { label: status, cls: "pen" };
  return <span className={`adcm-pill ${v.cls}`}>{v.label}</span>;
}

// ==== TOASTS (avisos) =========================================================
// tipos: "success" | "error" | "warn" | "info"
function useToasts() {
  const [toasts, setToasts] = useState([]);
  const idRef = useRef(0);

  const remove = (id) => setToasts((arr) => arr.filter((t) => t.id !== id));

  const push = (msg, type = "info", ms = 3500) => {
    const id = ++idRef.current;
    setToasts((arr) => [...arr, { id, msg, type }]);
    if (ms > 0) setTimeout(() => remove(id), ms);
  };

  return { toasts, push, remove };
}

export default function AdminComentarios() {
  const [status, setStatus] = useState("all");
  const [text, setText] = useState("");

  // NOVO: filtro de autores
  const [authorFilter, setAuthorFilter] = useState("all"); // 'all' | 'blocked' | 'unblocked'

  const [items, setItems] = useState([]);
  const [cursorPosts, setCursorPosts] = useState(null);
  const [cursorReplies, setCursorReplies] = useState(null);

  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const { toasts, push: pushToast, remove: removeToast } = useToasts();

  function mergeSort(a = [], b = []) {
    return [...a, ...b].sort((x, y) => (y.createdAtMs || 0) - (x.createdAtMs || 0));
  }

  async function runFetch(reset = false) {
    if (reset) {
      setLoading(true);
      try {
        const [{ items: posts, nextCursor: cPost }, { items: replies, nextCursor: cRep }] =
          await Promise.all([
            adminFetchPosts({ status, text, limitChunks: 4, cursor: null }),
            adminFetchReplies({ status, text, pageSize: 50, cursor: null }),
          ]);
        setItems(mergeSort(posts, replies));
        setCursorPosts(cPost);
        setCursorReplies(cRep);
      } catch (e) {
        pushToast("Falha ao carregar comentários.", "error");
        console.error(e);
      } finally {
        setLoading(false);
      }
      return;
    }

    if (!cursorPosts && !cursorReplies) return;

    setLoadingMore(true);
    try {
      const [postsRes, repliesRes] = await Promise.all([
        cursorPosts ? adminFetchPosts({ status, text, limitChunks: 4, cursor: cursorPosts }) : Promise.resolve({ items: [], nextCursor: null }),
        cursorReplies ? adminFetchReplies({ status, text, pageSize: 50, cursor: cursorReplies }) : Promise.resolve({ items: [], nextCursor: null }),
      ]);
      setItems((cur) => mergeSort(cur, mergeSort(postsRes.items, repliesRes.items)));
      setCursorPosts(postsRes.nextCursor);
      setCursorReplies(repliesRes.nextCursor);
    } catch (e) {
      pushToast("Falha ao carregar mais resultados.", "error");
      console.error(e);
    } finally {
      setLoadingMore(false);
    }
  }

  useEffect(() => {
    runFetch(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  // ===== Helpers padrão para ações =====
  const okPost = (msg) => pushToast(msg, "success");
  const errPost = (msg) => pushToast(msg, "error");

  // ===== AÇÕES POSTS =====
  async function handleApprove(p) {
    try {
      await adminApprove(p.postId);
      setItems((cur)=>cur.map(x=>x.postId===p.postId?{...x,status:"published"}:x));
      okPost("Post aprovado.");
    } catch (e) {
      errPost("Falha ao aprovar post."); console.error(e);
    }
  }
  async function handleReject(p) {
    try {
      await adminReject(p.postId);
      setItems((cur)=>cur.map(x=>x.postId===p.postId?{...x,status:"rejected"}:x));
      okPost("Post rejeitado.");
    } catch (e) {
      errPost("Falha ao rejeitar post."); console.error(e);
    }
  }
  async function handleSoftDelete(p) {
    try {
      await adminSoftDelete(p.postId);
      setItems((cur)=>cur.map(x=>x.postId===p.postId?{...x,status:"deleted"}:x));
      okPost("Post marcado como apagado (soft delete).");
    } catch (e) {
      errPost("Falha no soft delete do post."); console.error(e);
    }
  }
  async function handleHardDelete(p) {
    try {
      await adminHardDelete(p.postId);
      setItems((cur)=>cur.filter(x=>x.postId!==p.postId));
      okPost("Post removido em definitivo (hard delete).");
    } catch (e) {
      errPost("Falha no hard delete do post."); console.error(e);
    }
  }
  async function handleBlock(p) {
    try {
      await adminBlockUser(p.authorUid);
      // Propaga localmente o bloqueio para todos os itens do autor
      setItems((cur) =>
        cur.map((it) =>
          it.authorUid === p.authorUid ? { ...it, authorBlocked: true } : it
        )
      );
      okPost("Autor bloqueado.");
    } catch (e) {
      errPost("Falha ao bloquear autor."); console.error(e);
    }
  }
  async function handleUnblock(p) {
    try {
      await adminUnblockUser(p.authorUid);
      // Propaga localmente o desbloqueio para todos os itens do autor
      setItems((cur) =>
        cur.map((it) =>
          it.authorUid === p.authorUid ? { ...it, authorBlocked: false } : it
        )
      );
      okPost("Autor desbloqueado.");
    } catch (e) {
      errPost("Falha ao desbloquear autor."); console.error(e);
    }
  }

  // ===== AÇÕES REPLIES =====
  async function handleApproveReply(p){
    try {
      await adminApproveReply(p.postId, p.replyId);
      updateReplyStatusLocal(p, "published");
      okPost("Reply aprovado.");
    } catch (e) { errPost("Falha ao aprovar reply."); console.error(e); }
  }
  async function handleRejectReply(p){
    try {
      await adminRejectReply(p.postId, p.replyId);
      updateReplyStatusLocal(p, "rejected");
      okPost("Reply rejeitado.");
    } catch (e) { errPost("Falha ao rejeitar reply."); console.error(e); }
  }
  async function handleSoftDeleteReply(p){
    try {
      await adminSoftDeleteReply(p.postId, p.replyId);
      updateReplyStatusLocal(p, "deleted");
      okPost("Reply marcado como apagado (soft delete).");
    } catch (e) { errPost("Falha no soft delete do reply."); console.error(e); }
  }
  function updateReplyStatusLocal(p, newStatus) {
    setItems((cur) =>
      cur.map((it) =>
        it.replyId === p.replyId ? { ...it, status: newStatus } : it
      )
    );
  }

  async function handleSearchSubmit(e){
    e.preventDefault();
    await runFetch(true);
    if (text?.trim()) {
      pushToast(`Busca atualizada para “${text.trim()}”.`, "info", 2500);
    } else {
      pushToast("Filtro atualizado.", "info", 2000);
    }
  }

  // ===== filtro de autores antes de renderizar =====
  const filteredItems = items.filter((it) => {
    const blocked = !!it.authorBlocked; // undefined -> false
    if (authorFilter === "blocked") return blocked;
    if (authorFilter === "unblocked") return !blocked;
    return true; // all
  });

  const canLoadMore = !!(cursorPosts || cursorReplies);

  return (
    <div className="adcm-wrap">
      <header className="adcm-head">
        <div className="adcm-title">
          <img src={ICONS.timeline} alt="" />
          <h2>Comentários (Timeline)</h2>
        </div>

        <form className="adcm-filters" onSubmit={handleSearchSubmit}>
          <select className="adcm-select" value={status} onChange={(e)=>setStatus(e.target.value)}>
            <option value="all">Todos</option>
            <option value="pending">Pendentes</option>
            <option value="published">Publicados</option>
            <option value="rejected">Rejeitados</option>
            <option value="deleted">Apagados</option>
          </select>

          {/* NOVO: filtro de autores */}
          <select
            className="adcm-select"
            value={authorFilter}
            onChange={(e)=>setAuthorFilter(e.target.value)}
            aria-label="Filtrar por autores bloqueados"
          >
            <option value="all">Autores — Todos</option>
            <option value="blocked">Apenas bloqueados</option>
            <option value="unblocked">Apenas não bloqueados</option>
          </select>

          <input
            className="adcm-input"
            placeholder="Buscar por autor, texto ou categoria…"
            value={text}
            onChange={(e)=>setText(e.target.value)}
          />
          <button className="adcm-btn" type="submit">Buscar</button>
        </form>
      </header>

      {/* Região de mensagens para acessibilidade */}
      <div className="sr-only" aria-live="polite" aria-atomic="true">
        {toasts.length ? toasts[toasts.length - 1].msg : ""}
      </div>

      {loading ? (
        <div className="adcm-loading">Carregando…</div>
      ) : filteredItems.length === 0 ? (
        <div className="adcm-empty">Nada encontrado.</div>
      ) : (
        <ul className="adcm-list">
          {filteredItems.map((p) => (
            <li key={p.replyId || p.postId} className="adcm-card">
              <div className="adcm-meta">
                <div className="adcm-author">
                  {p.authorAvatar ? (
                    <img src={p.authorAvatar} alt={p.authorName} />
                  ) : (
                    <div className="adcm-avatar-ph" />
                  )}
                  <div className="adcm-author-text">
                    <div className="adcm-author-name">
                      {p.authorName}{" "}
                      {p.especialista && <span className="adcm-badge">ESPECIALISTA OSCAR</span>}
                      {p.authorBlocked && <span className="adcm-badge blocked">AUTOR BLOQUEADO</span>}
                    </div>
                    <div className="adcm-date">
                      {formatPtDate(p.createdAtDate || p.createdAt || p.createdAtMs)}
                    </div>
                  </div>
                </div>

                <div className="adcm-right">
                  <div className={`adcm-kind ${p.isReply ? "is-reply" : "is-post"}`}>
                    {p.isReply ? "💬 Reply" : "🗨️ Post"}
                    {p.isReply && p.parentPostId && (
                      <small className="adcm-parent">em {String(p.parentPostId).slice(0, 6)}…</small>
                    )}
                  </div>
                  <StatusPill status={p.status} />
                </div>
              </div>

              {p.text && <div className="adcm-text">{p.text}</div>}

              {!p.isReply && p.attach && (
                <div className="adcm-attach">
                  <div className="adcm-attach-head">
                    <span className="adcm-attach-mode">Apostas – {prettyModo(p.attach.modo)}</span>
                    <span className="adcm-attach-cat">
                      {(prettyCategoria(p.attach.categoria) || "").toUpperCase()}
                    </span>
                  </div>
                  <div className="adcm-attach-count">
                    {Array.isArray(p.attach.itens) ? `${p.attach.itens.length} candidatos` : "—"}
                  </div>
                </div>
              )}

              <div className="adcm-actions">
                {p.isReply ? (
                  <>
                    {p.status !== "published" && (
                      <button className="adcm-btn ok" onClick={()=>handleApproveReply(p)}>Aprovar</button>
                    )}
                    {p.status !== "rejected" && (
                      <button className="adcm-btn warn" onClick={()=>handleRejectReply(p)}>Rejeitar</button>
                    )}
                    {p.status !== "deleted" && (
                      <button className="adcm-btn danger" onClick={()=>handleSoftDeleteReply(p)}>Soft delete</button>
                    )}
                  </>
                ) : (
                  <>
                    {p.status !== "published" && (
                      <button className="adcm-btn ok" onClick={()=>handleApprove(p)}>Aprovar</button>
                    )}
                    {p.status !== "rejected" && (
                      <button className="adcm-btn warn" onClick={()=>handleReject(p)}>Rejeitar</button>
                    )}
                    {p.status !== "deleted" && (
                      <button className="adcm-btn danger" onClick={()=>handleSoftDelete(p)}>Soft delete</button>
                    )}
                    <button className="adcm-btn danger-outline" onClick={()=>handleHardDelete(p)}>Hard delete</button>
                  </>
                )}

                <div className="adcm-spacer" />
                <button className="adcm-btn gray" onClick={()=>handleBlock(p)}>Bloquear autor</button>
                <button className="adcm-btn gray" onClick={()=>handleUnblock(p)}>Desbloquear autor</button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <div className="adcm-more">
        <button
          className="adcm-btn"
          disabled={!canLoadMore || loadingMore}
          onClick={()=>runFetch(false)}
        >
          {loadingMore ? "Carregando…" : canLoadMore ? "Carregar mais" : "Fim da lista"}
        </button>
      </div>

      {/* TOAST CONTAINER */}
      <div className="adcm-toasts" role="status" aria-live="polite" aria-atomic="true">
        {toasts.map((t) => (
          <div key={t.id} className={`adcm-toast ${t.type}`}>
            <span className="adcm-toast-msg">{t.msg}</span>
            <button
              className="adcm-toast-x"
              onClick={()=>removeToast(t.id)}
              aria-label="Fechar aviso"
              title="Fechar"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
